// frontend/src/components/TemplateEditor.jsx
import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../context/AuthContext.jsx';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import logger from '../utils/logger';

function TemplateEditor({ template, onSave, onCancel, variables = [] }) {
  const { token } = useContext(AuthContext);
  const [content, setContent] = useState(template?.template || '');
  const [name, setName] = useState(template?.name || '');
  const [description, setDescription] = useState(template?.description || '');
  const [feature, setFeature] = useState(template?.feature || 'chat');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState('');
  const [previewContext, setPreviewContext] = useState({});

  // Initialize preview context with default values
  useEffect(() => {
    const defaultContext = {};
    variables.forEach(variable => {
      if (variable.defaultValue) {
        defaultContext[variable.name] = variable.defaultValue;
      } else {
        defaultContext[variable.name] = '';
      }
    });
    setPreviewContext(defaultContext);
  }, [variables]);

  // Helper function to decode HTML entities
  const decodeHtmlEntities = (text) => {
    if (!text) return text;
    const textarea = document.createElement('textarea');
    textarea.innerHTML = text;
    return textarea.value;
  };

  // Helper function to strip HTML tags but preserve line breaks
  const stripHtmlPreserveBreaks = (html) => {
    if (!html) return html;
    // Decode entities first
    const decoded = decodeHtmlEntities(html);
    // Replace <br>, <p>, <div> with newlines, then strip remaining tags
    return decoded
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<\/p>/gi, '\n')
      .replace(/<\/div>/gi, '\n')
      .replace(/<p[^>]*>/gi, '')
      .replace(/<div[^>]*>/gi, '')
      .replace(/<[^>]+>/g, '')
      .replace(/&nbsp;/g, '\n')
      .trim();
  };

  // Update preview when content or context changes
  useEffect(() => {
    if (content && Object.keys(previewContext).length > 0) {
      // Simple preview - replace variables
      let previewText = content;
      Object.keys(previewContext).forEach(key => {
        const regex = new RegExp(`\\{\\{${key}\\}\\}`, 'g');
        previewText = previewText.replace(regex, previewContext[key] || `{{${key}}}`);
      });
      // Decode HTML entities and strip HTML tags for clean preview
      setPreview(stripHtmlPreserveBreaks(previewText));
    } else {
      setPreview(stripHtmlPreserveBreaks(content));
    }
  }, [content, previewContext]);

  const handleInsertVariable = (variableName) => {
    const quill = document.querySelector('.ql-editor');
    if (quill) {
      const selection = window.getSelection();
      const range = selection.getRangeAt(0);
      const textNode = document.createTextNode(`{{${variableName}}}`);
      range.insertNode(textNode);
      range.setStartAfter(textNode);
      range.setEndAfter(textNode);
      selection.removeAllRanges();
      selection.addRange(range);
    }
    // Also update ReactQuill content
    const editor = document.querySelector('.ql-editor');
    if (editor) {
      const currentContent = editor.innerHTML;
      const cursorPos = editor.textContent.length; // Simple approach
      const newContent = currentContent + `{{${variableName}}}`;
      setContent(newContent);
    }
  };

  const handleSave = async () => {
    if (!name.trim()) {
      setError('Template name is required');
      return;
    }
    if (!content.trim()) {
      setError('Template content is required');
      return;
    }

    setIsSaving(true);
    setError('');

    try {
      await onSave({
        name,
        description,
        feature,
        template: content,
        variables: extractVariables(content)
      });
    } catch (err) {
      logger.error('Error saving template:', err);
      setError(err.message || 'Failed to save template');
    } finally {
      setIsSaving(false);
    }
  };

  const extractVariables = (templateText) => {
    const variableRegex = /\{\{(\w+)\}\}/g;
    const found = new Set();
    let match;
    while ((match = variableRegex.exec(templateText)) !== null) {
      found.add(match[1]);
    }
    return Array.from(found).map(varName => ({
      name: varName,
      description: variables.find(v => v.name === varName)?.description || '',
      required: false,
      defaultValue: variables.find(v => v.name === varName)?.defaultValue || ''
    }));
  };

  // Simplified toolbar config for old react-quill version compatibility
  const quillModules = {
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ list: 'ordered'}, { list: 'bullet' }],
      ['blockquote', 'code-block'],
      ['link'],
      ['clean']
    ]
  };

  return (
    <div className="flex flex-col h-full bg-white dark:bg-gray-800">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            {template?._id ? 'Edit Template' : 'Create New Template'}
          </h2>
          <div className="flex gap-2">
            <button
              onClick={onCancel}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
            <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
          </div>
        )}

        {/* Basic Info */}
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Template Name *
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              placeholder="e.g., Chat Prompt v2"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Feature
            </label>
            <select
              value={feature}
              onChange={(e) => setFeature(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="chat">Chat</option>
              <option value="autofill">Auto-Fill</option>
              <option value="feedback">Feedback</option>
              <option value="summary">Summary</option>
            </select>
          </div>
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Description
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            rows="2"
            placeholder="Brief description of this template..."
          />
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex overflow-hidden">
        {/* Editor Panel */}
        <div className="flex-1 flex flex-col border-r border-gray-200 dark:border-gray-700">
          <div className="p-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">Template Editor</h3>
              {variables.length > 0 && (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-700 dark:text-gray-400">Insert Variable:</span>
                  <select
                    onChange={(e) => {
                      if (e.target.value) {
                        handleInsertVariable(e.target.value);
                        e.target.value = '';
                      }
                    }}
                    className="text-xs px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-700 dark:text-white"
                  >
                    <option value="">Select variable...</option>
                    {variables.map(variable => (
                      <option key={variable.name} value={variable.name}>
                        {variable.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </div>
          <div className="flex-1 overflow-auto p-4">
            <style>{`
              .ql-editor {
                color: rgb(17, 24, 39) !important; /* text-gray-900 */
                min-height: 200px;
              }
              .dark .ql-editor {
                color: rgb(243, 244, 246) !important; /* text-gray-100 */
              }
              .ql-snow .ql-stroke {
                stroke: rgb(107, 114, 128) !important; /* text-gray-500 */
              }
              .dark .ql-snow .ql-stroke {
                stroke: rgb(156, 163, 175) !important; /* text-gray-400 */
              }
              .ql-snow .ql-fill {
                fill: rgb(107, 114, 128) !important;
              }
              .dark .ql-snow .ql-fill {
                fill: rgb(156, 163, 175) !important;
              }
            `}</style>
            <ReactQuill
              theme="snow"
              value={content}
              onChange={setContent}
              modules={quillModules}
              className="h-full"
              style={{ height: 'calc(100% - 42px)' }}
            />
          </div>
        </div>

        {/* Preview Panel */}
        <div className="flex-1 flex flex-col">
          <div className="p-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">Preview</h3>
          </div>
          <div className="flex-1 overflow-auto p-4">
            <div className="prose dark:prose-invert max-w-none">
              <pre className="whitespace-pre-wrap text-sm text-gray-900 dark:text-gray-100 bg-gray-50 dark:bg-gray-900 p-4 rounded border border-gray-200 dark:border-gray-700">
                {preview || 'Preview will appear here...'}
              </pre>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TemplateEditor;
